import React, {createContext, useState, useContext, ReactNode, useEffect} from 'react';
import {MamData, MamSummary, MatchDays} from '../types/mamTypes';
import VideoContentTags from "../components/CommonComponents/VideoContentTags/VideoContentTags";
import {VideoContentTypes} from "../types/clipTypes";
import {updateClipTags} from "../queries/clipQueries";
import {useVideoContentTagsOptions} from "./VideoContentTagsOptions";
interface MamInformationProps {
    information: MamData | undefined;
    setInformation: (information: MamData) => void;
    setCompetition: (competition: string[]) => void;
    setMatchDay: (matchDay: MatchDays[]) => void;
    setMatchDayEvents: (matchDayEvents: any[]) => void;
    setClipGroups: (clipGroups: any[]) => void;
    setClip: (clip: any[]) => void;
    setDeleteLoading: (loading: boolean) => void;
    deleteLoading: boolean;
    updateLoading: boolean;
    setUpdateLoading: (loading: boolean) => void;
    updateVideoContentTags: (videoContentId: string, videoContentType: VideoContentTypes, newTags: string[]) => void;
}

const MamInformationContext = createContext<MamInformationProps | undefined>(undefined);

const MamInformationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {

    const [information, setInformation] = useState<MamData>({
        competitions: [],
        matchDay: [],
        matchDayEvents: [],
        clipGroups: [],
        clip: []
    });

    const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
    const [updateLoading, setUpdateLoading] = useState<boolean>(false);

    const {addVideoContentTagsOptions} = useVideoContentTagsOptions()

    function setCompetition(competition: string[]) {
        setInformation((prevState) => ({
            ...prevState,
            competition: Array.from(new Set([...(prevState.competitions || []), ...competition]))
        }));
    }
    function setMatchDayEvents(matchDayEvents: any[]) {
        setInformation((prevState) => ({
            ...prevState,
            matchDayEvents: Array.from(new Set([...(prevState.matchDayEvents || []), ...matchDayEvents]))
        }));
    }
    function setMatchDay(matchDay: MatchDays[]) {
        setInformation((prevState) => ({
            ...prevState,
            matchDay: Array.from(new Set([...(prevState.matchDay || []), ...matchDay]))
        }));
    }

    function setClipGroups(clipGroups: any[]) {
        setInformation((prevState) => ({
            ...prevState,
            clipGroups: Array.from(new Set([...(prevState.clipGroups || []), ...clipGroups]))
        }));
    }

    function setClip(clip: any[]) {
        setInformation((prevState) => ({
            ...prevState,
            clip: Array.from(new Set([...(prevState.clip || []), ...clip]))
        }));
    }

    async function updateVideoContentTags(videoContentId: string, videoContentType: VideoContentTypes, newTags: string[], type?: string) {
        const tempInformation = {...information};
        const videoContent = videoContentType !== 'socialClip' ?
            tempInformation.clipGroups.find(videoContent => videoContent.id === videoContentId) :
            tempInformation.clip.find(videoContent => videoContent.id === videoContentId)
        const socialHighlight = tempInformation.clip.find(videoContent => videoContent.id === videoContentId)
        if (videoContent){
            setUpdateLoading(true)
            const prevTags = videoContent.tags ? JSON.parse(JSON.stringify(videoContent.tags)) : []
            try{
                videoContent.tags = newTags
                if (socialHighlight){
                    socialHighlight.tags = newTags
                }
                setInformation(tempInformation)
                await updateClipTags(videoContentId, newTags, videoContentType);
                addVideoContentTagsOptions(newTags)
            }catch (e){
                videoContent.tags = prevTags
                if (socialHighlight){
                    socialHighlight.tags = prevTags
                }
                setInformation(tempInformation)
            }finally {
                setUpdateLoading(false)
            }
        }
    }


    return (
        <MamInformationContext.Provider value={{information, setInformation, setMatchDayEvents,setMatchDay, setClipGroups, setCompetition, setClip, setDeleteLoading, deleteLoading, updateLoading, setUpdateLoading,
            updateVideoContentTags}}>
            {children}
        </MamInformationContext.Provider>
    );
};

// Custom hook to use the filter context
const useMamInformation = () => {
    const context = useContext(MamInformationContext);
    if (context === undefined) {
        throw new Error('useMamInformation must be used within a MamInformation provider');
    }
    return context;
};

export { MamInformationProvider, useMamInformation };